import { useSyncExternalStore } from 'react'

import { env } from '~publish/env'

const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

export type VersionPollingStore = {
  subscribe: (listener: () => void) => () => void
  getSnapshot: () => boolean
}

/**
 * Creates a store that polls `/version.json` while it has subscribers and
 * flips its snapshot to true once the served `version` differs from the
 * baseline established by the first successful fetch.
 *
 * Application code uses the shared module-level instance via
 * `useUpdateAvailable`; tests construct a fresh store per test to avoid
 * state leaking between them.
 */
export function createVersionPollingStore(): VersionPollingStore {
  let baselineVersion: string | null = null
  let updateAvailable = false
  let intervalId: ReturnType<typeof setInterval> | null = null
  let checkInFlight = false
  const listeners = new Set<() => void>()

  async function checkVersion(): Promise<void> {
    if (updateAvailable || checkInFlight) return
    checkInFlight = true
    try {
      // Plain URL on purpose: nginx serves /version.json with a short
      // max-age (see docker/nginx-config/publish-frontend), so repeat
      // requests within that window are served from cache instead of
      // busting it with a unique query param.
      const res = await fetch('/version.json')
      if (!res.ok) return

      // Guard against SPA fallback returning HTML instead of JSON
      // (e.g. Vite dev server or Nginx try_files serving index.html).
      const contentType = res.headers.get('content-type') ?? ''
      if (!contentType.includes('application/json')) return

      const data: { version?: string } = await res.json()
      if (!data.version) return

      if (baselineVersion === null) {
        baselineVersion = data.version
        return
      }

      if (data.version !== baselineVersion) {
        updateAvailable = true
        stopPolling()
        listeners.forEach((notify) => notify())
      }
    } catch {
      // Network errors are expected (offline, etc.) — silently ignore.
    } finally {
      checkInFlight = false
    }
  }

  function startPolling(): void {
    if (intervalId || updateAvailable) return
    intervalId = setInterval(() => void checkVersion(), POLL_INTERVAL_MS)
  }

  function stopPolling(): void {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  function handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      // Check immediately when the tab becomes visible again, then resume.
      void checkVersion()
      startPolling()
    } else {
      stopPolling()
    }
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener)
    if (listeners.size === 1) {
      // Poll only while the tab is visible — if the page loads hidden, the
      // visibilitychange handler starts checking once it becomes visible.
      // Only fetch immediately while no baseline exists; a resubscribe after
      // a remount resumes polling against the baseline established at load.
      if (document.visibilityState === 'visible') {
        if (baselineVersion === null) void checkVersion()
        startPolling()
      }
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }
    return (): void => {
      listeners.delete(listener)
      if (listeners.size === 0) {
        stopPolling()
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
  }

  return { subscribe, getSnapshot: (): boolean => updateAvailable }
}

// Single shared instance: however many components call the hook — or however
// often they remount (the app subtree remounts during boot and on org switch,
// see CORE-6262) — there is one baseline, one interval, and one in-flight
// check. Keeping the baseline outside React also means a remount cannot
// re-baseline onto a freshly bumped version and swallow the update notice.
const appVersionPollingStore = createVersionPollingStore()

const subscribeDisabled = (): (() => void) => (): void => {}
const getSnapshotDisabled = (): boolean => false

/**
 * Polls `/version.json` to detect when a newer version of the app has been
 * deployed. The first successful response establishes a baseline; once a later
 * poll returns a different `version`, `updateAvailable` flips to true.
 *
 * `version.json` is currently bumped manually — per-deploy updates would
 * prompt users too often. See `scripts/vite-plugin-version.mjs` for the
 * (paused) build-time automation.
 *
 * Polling pauses while the tab is hidden and stops entirely once an update is
 * detected. Skipped in development/test environments.
 *
 * @param store - injectable so tests can use an isolated store; application
 * code omits it and shares the app-wide instance.
 */
export function useUpdateAvailable(
  store: VersionPollingStore = appVersionPollingStore,
): { updateAvailable: boolean } {
  const disabled =
    env.VITE_NODE_ENV === 'development' || env.VITE_NODE_ENV === 'test'

  const updateAvailable = useSyncExternalStore(
    disabled ? subscribeDisabled : store.subscribe,
    disabled ? getSnapshotDisabled : store.getSnapshot,
  )

  return { updateAvailable }
}
